import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { api, errMessage, fileUrl } from "../api";
import { fmtDayLong, fmtTimeRange, kindLabel } from "../lib/dates";
import {
  Footer,
  Loading,
  Nav,
  Verse,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUS_COPY = {
  confirmed: {
    title: "You're in.",
    body: "We'll expect you. If plans change, cancel below \u2014 it frees the seat for someone on the waitlist.",
  },
  waitlist: {
    title: "You're on the waitlist.",
    body: "If a seat opens, we'll email you. No need to hover.",
  },
  cancelled: {
    title: "This booking is cancelled.",
    body: "Nothing to do. If you'd like to come after all, RSVP again.",
  },
  pending: {
    title: "We're still processing this one.",
    body: "Give it a moment and reload.",
  },
};

export default function RsvpManage() {
  const { token } = useParams();
  useBodyClass("inner");
  useDocumentTitle("Your booking");

  const { data, error, isLoading, mutate } = useSWR(`/rsvp/${token}`, fetcher);
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState("");

  const cancel = async () => {
    setFailure("");
    setWorking(true);
    try {
      await api.post(`/rsvp/${token}/cancel`);
      await mutate();
    } catch (err) {
      setFailure(errMessage(err, "We couldn't cancel that just now."));
    } finally {
      setWorking(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Nav />
        <Loading label="Finding your booking" testid="rsvp-manage-loading" />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <header className="page-head">
          <div className="prose">
            <p className="eyebrow sc">
              <span className="num">&middot;</span>
              <span>Not found</span>
            </p>
            <h1>We can&rsquo;t find that booking.</h1>
            <p className="standfirst">
              The link may be mistyped, or the gathering may have been taken down.
            </p>
          </div>
        </header>
        <section data-testid="rsvp-manage-notfound">
          <div className="prose">
            <Link to="/gatherings" className="act">
              See what&rsquo;s on &rarr;
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const { rsvp, gathering } = data;
  const copy = STATUS_COPY[rsvp.status] || STATUS_COPY.pending;
  const canCancel = rsvp.status === "confirmed" || rsvp.status === "waitlist";

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="rsvp-manage-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>Your booking</span>
          </p>
          <h1>{copy.title}</h1>
          <p className="standfirst">{copy.body}</p>
        </div>
      </header>

      <section data-testid="rsvp-manage-body">
        <div className="prose">
          {gathering ? (
            <dl className="gathering-meta">
              <div>
                <dt>Gathering</dt>
                <dd>
                  <Link to={`/gatherings/${gathering.id}`}>{gathering.title}</Link>
                  <br />
                  <span className="chip" style={{ marginTop: ".5rem" }}>
                    {kindLabel(gathering.kind)}
                  </span>
                </dd>
              </div>
              <div>
                <dt>When</dt>
                <dd data-testid="rsvp-when">
                  {gathering.when_text ? (
                    <>
                      {gathering.when_text}
                      <br />
                      <em>date to be confirmed</em>
                    </>
                  ) : (
                    <>
                      {fmtDayLong(gathering.starts_at)}
                      <br />
                      {fmtTimeRange(gathering.starts_at, gathering.ends_at)}
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt>In your name</dt>
                <dd>
                  {rsvp.name}
                  <br />
                  {rsvp.seats} {rsvp.seats === 1 ? "person" : "people"}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="empty">
              <p>That gathering is no longer listed.</p>
            </div>
          )}

          <div className="actions">
            {gathering && rsvp.status !== "cancelled" ? (
              <a
                className="act"
                href={fileUrl(`/gatherings/${gathering.id}/calendar.ics`)}
                data-testid="rsvp-manage-calendar-link"
              >
                Add to calendar (.ics)
              </a>
            ) : null}
            {canCancel ? (
              <button
                type="button"
                className="act act--danger"
                onClick={cancel}
                disabled={working}
                data-testid="rsvp-manage-cancel-button"
              >
                {working ? "Cancelling\u2026" : "Cancel this booking"}
              </button>
            ) : null}
            <Link className="linkish" to="/gatherings">
              See what else is on
            </Link>
          </div>

          {failure ? (
            <div className="form-error" data-testid="rsvp-manage-error">
              {failure}
            </div>
          ) : null}

          {rsvp.status === "cancelled" && gathering ? (
            <div className="notice" data-testid="rsvp-manage-cancelled-note">
              Changed your mind again? You can{" "}
              <Link to={`/gatherings/${gathering.id}`}>RSVP to this one afresh</Link>.
            </div>
          ) : null}
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
